// /start, Getting ready (spec "Getting ready, the setup gate and the dashboard"): straight after signing up. The
// cloud machine comes up live while the page asks where setup runs: the Mac app (recommended on a Mac), the Windows
// app (recommended on Windows) or cmx in a terminal (for power users, and the way on Linux). All run the same Magic
// Setup; the page follows the journey (/api/me) and moves on by itself when a sign-in is approved and when setup
// ends.

import { useEffect, useState, type ReactNode } from 'react';
import { billing, installCommand, mustSetUp, needsPlan, reportOnboarded } from '../api';
import { setupWays, thisDevice, type Device, type Way } from '../device';
import { Link, Redirect, navigate, useLocation } from '../router';
import { refreshMe, useSession } from '../session';
import { CodeRow } from '../ui/CodeRow';
import { PageSplash, signOut } from './Shell';
import { IconBrandWindows, IconCheck, IconDeviceDesktop } from '@tabler/icons-react';
import { HxPage, HxWindow, Icon } from './hx';
import { SetupArt } from './SetupArt';
import './start.css';
import { AppDownload } from '../ui/AppDownload';
import { WayArt } from './GetStarted';
import { LIFE, useMachineLife } from './machineLife';

// The pick, remembered in this browser (coming back opens on it).
const WAY_KEY = 'cmx.start.way';
function savedWay(): Way | null {
  try {
    const v = localStorage.getItem(WAY_KEY);
    return v === 'mac' || v === 'win' || v === 'cli' ? v : null;
  } catch {
    return null;
  }
}
function saveWay(w: Way) {
  try {
    localStorage.setItem(WAY_KEY, w);
  } catch {
    /* private window: just not remembered */
  }
}

/** Where each way continues, in the title once a sign-in is approved and on the waiting line before. */
const PLACE: Record<Way, { title: string; waiting: string }> = {
  mac: { title: 'the Mac app.', waiting: 'Waiting for the Mac app to sign in…' },
  win: { title: 'the Windows app.', waiting: 'Waiting for the Windows app to sign in…' },
  cli: { title: 'your terminal.', waiting: 'Waiting for cmx to sign in…' },
};

/** Back from Stripe Checkout (?checkout=<session>): the plan is applied now, not when the webhook comes. */
function useCheckoutReturn(signedIn: boolean) {
  const { search } = useLocation();
  const session = search.get('checkout');
  const [syncing, setSyncing] = useState(!!session);
  useEffect(() => {
    if (!signedIn || !session) return;
    void billing.sync(session).then(async () => {
      await refreshMe();
      setSyncing(false);
      history.replaceState(null, '', '/start');
    });
  }, [signedIn, session]);
  return syncing;
}

export function StartPage() {
  const { auth, me } = useSession();
  const signedIn = auth.kind === 'in' && !!me;
  const syncing = useCheckoutReturn(signedIn);
  const { life, id } = useMachineLife(signedIn);
  const [device] = useState(thisDevice);
  const [picked, setPicked] = useState<Way | null>(savedWay);
  const [leaving, setLeaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Follow the journey: a sign-in approved, then setup ending.
  useEffect(() => {
    if (!signedIn) return;
    const t = setInterval(() => void refreshMe(), 2500);
    return () => clearInterval(t);
  }, [signedIn]);

  if (auth.kind === 'out') return <Redirect to="/login?next=%2Fstart" />;
  if (auth.kind === 'error') return <PageSplash title="Can’t reach cmx" detail={`${auth.message} — retrying…`} />;
  if (!signedIn || !me || syncing) return <PageSplash title={syncing ? 'Confirming your payment…' : undefined} />;
  if (needsPlan(me)) return <Redirect to="/plans" />;

  const appIn = me.journey.includes('signin');
  const done = me.setup.done;
  const required = mustSetUp(me);
  const ready = life === 'ready';
  const mobile = device === 'mobile';
  // An app can't be picked until it ships (apps): the ways on offer here, the recommended one unless another was picked.
  const { ways, recommended } = setupWays(device, me.apps);
  const way = picked && ways.includes(picked) ? picked : recommended;
  const pick = (w: Way) => {
    setPicked(w);
    saveWay(w);
  };

  /** "Use the browser for now": counts as the end of onboarding (so the gate never locks them later). */
  const useBrowser = async () => {
    setLeaving(true);
    setError(null);
    const r = await reportOnboarded();
    if (!r.ok) {
      setLeaving(false);
      setError(r.error);
      return;
    }
    await refreshMe();
    navigate('/dashboard', { replace: true });
  };

  const title = done ? (
    <>
      You’re <em>set up.</em>
    </>
  ) : appIn ? (
    <>
      Continue in <em>{PLACE[way ?? 'cli'].title}</em>
    </>
  ) : ready ? (
    <>
      Set up <em>your cloud machine.</em>
    </>
  ) : life === 'reserved' ? (
    <>
      Your cloud machine <em>is reserved.</em>
    </>
  ) : (
    <>
      Your cloud machine <em>is starting.</em>
    </>
  );
  const lead = done
    ? 'Your cloud machine is ready, and so is the web. Pick up anywhere.'
    : appIn
      ? 'Signed in. Magic Setup is moving your setup over; this page opens up when it’s done.'
      : mobile
        ? 'Setup runs on a Mac, Windows or Linux computer: the Mac or Windows app, or cmx in a terminal. Sign in at cmx.sh there to start.'
        : ways.length > 1
          ? 'Magic Setup moves your shell, agents, projects and sign-ins to your cloud machine. Pick where it runs.'
          : 'Magic Setup moves your shell, agents, projects and sign-ins to your cloud machine, from cmx in your terminal.';
  // Browser-only while the gate doesn't apply, or on a phone.
  const browser = !done && (!required || mobile);

  const lifeInfo = LIFE[life];
  return (
    <HxPage
      testid="start"
      top={
        <>
          <Link to="/account" className="hx-btn hx-btn-quiet hx-btn-sm" data-testid="start-account">
            Account
          </Link>
          <button className="hx-btn hx-btn-quiet hx-btn-sm" onClick={() => void signOut()} data-testid="start-sign-out">
            Sign out
          </button>
        </>
      }
    >
      <div className="hx-column is-wide" data-step={done ? 'done' : appIn ? 'app' : 'choose'} data-machine={life} data-device={device}>
        <div className="hx-column-head hx-rise">
          <SetupArt />
          <h1 className="hx-h1" data-testid="start-title">
            {title}
          </h1>
          <p>{lead}</p>
        </div>

        {done ? (
          // Set up: no panel, just the two ways on (Docs-Start-Done).
          <div className="hx-rise" style={{ ['--i' as string]: 1 }}>
            <div className="hx-cta" data-testid="start-done">
              <Link to="/dashboard" className="hx-btn hx-btn-primary" data-testid="start-open-dashboard">
                Open your dashboard
              </Link>
              <a href="/app" className="hx-btn hx-btn-secondary">
                Open the workspace
              </a>
            </div>
          </div>
        ) : (
        <div className="hx-rise" style={{ ['--i' as string]: 1 }}>
          <HxWindow>
                <section className="hx-start-machine" data-testid="start-machine" data-state={life}>
          <div className="hx-start-row">
            <span className={`hx-tile ${ready ? 'hx-tile-green' : 'hx-tile-blue'}`}><IconDeviceDesktop size={20} stroke={1.5} aria-hidden="true" /></span>
            <span className="hx-start-text">
              <b>Your cloud machine{id ? ` · ${id}` : ''}</b>
              <span>{ready ? 'Always on. It keeps working while this computer sleeps.' : life === 'reserved' ? 'Linux with the agents, Docker and Chrome.' : 'Linux with the agents, Docker and Chrome, ready in about a minute.'}</span>
            </span>
            <span className={`hx-start-state ${ready ? 'is-ready' : ''}`} data-testid="start-machine-state">
              {!ready && <span className="hx-spin" aria-hidden="true" />}
              {ready && <IconCheck size={14} stroke={2} aria-hidden="true" />}
              {lifeInfo.label}
            </span>
          </div>
          {/* the progress only while it comes up; once ready the pill says it (as the Mac's SetupMachineCard) */}
          {!ready && (
            <>
              <div className="hx-start-bar" aria-hidden="true">
                <i style={{ transform: `scaleX(${lifeInfo.at})` }} />
              </div>
              <div className="hx-start-stages" aria-hidden="true">
                <span data-on={lifeInfo.at >= 0.34}>Make</span>
                <span data-on={lifeInfo.at >= 0.72}>Start</span>
                <span data-on={false}>Ready</span>
              </div>
            </>
          )}
          {life === 'reserved' ? (
            <p className="hx-start-note" data-testid="start-reserved">
              We’re reserving your cloud machine. We’ll email you as soon as it’s ready, and this page updates by itself.
            </p>
          ) : life === 'full' || life === 'none' ? (
            <p className="hx-start-note">We’re making room for it. This page updates by itself.</p>
          ) : life === 'error' ? (
            <p className="hx-start-note">Something went wrong starting it. We’ve been told; it will retry.</p>
          ) : null}
        </section>

        {/* a choice only when there is one: no greyed-out card for a way that can't be taken */}
        {ways.length > 1 && <Ways device={device} ways={ways} recommended={recommended} way={way} onPick={pick} />}
        {way && <WayPanel key={way} way={way} device={device} appIn={appIn} email={me.email} />}

          </HxWindow>
        </div>
        )}

        {error && (
          <div className="hx-error" role="alert">
            {error}
          </div>
        )}
        {browser && (
          <div className="hx-start-alt hx-rise" style={{ ['--i' as string]: 4 }}>
            <button className="hx-btn hx-btn-quiet" onClick={() => void useBrowser()} disabled={leaving} data-testid="start-use-browser">
              {leaving ? 'Opening…' : mobile ? 'Use the browser for now' : 'Continue in the browser instead'}
            </button>
          </div>
        )}
      </div>
    </HxPage>
  );
}

/** The ways, as a choice; the one this device should take comes first, marked Recommended. */
function Ways({ device, ways, recommended: rec, way, onPick }: { device: Device; ways: Way[]; recommended: Way | null; way: Way | null; onPick: (w: Way) => void }) {
  const card: Record<Way, { icon: ReactNode; title: string; detail: string; badge: string | null }> = {
    mac: {
      icon: <WayArt kind="app" />,
      title: 'Mac app',
      detail: 'Terminal, desktop and previews in one window. It sets up your cloud machine like your Mac.',
      badge: rec === 'mac' ? 'Recommended' : device === 'mac' ? null : 'For Mac',
    },
    win: {
      icon: <WayArt kind="windows" />,
      title: 'Windows app',
      detail: 'Terminal, desktop and previews in one window. It sets up your cloud machine like your PC, WSL included.',
      badge: rec === 'win' ? 'Recommended' : device === 'windows' ? null : 'For Windows',
    },
    cli: {
      icon: <WayArt kind="terminal" />,
      title: 'Terminal',
      detail: device === 'windows' ? 'cmx in your own terminal, in WSL.' : 'cmx in your own terminal, on macOS, Linux, or Windows with WSL.',
      badge: rec === 'cli' ? 'Recommended' : 'For power users',
    },
  };
  return (
    <div className="hx-start-ways" role="group" aria-label="Where to set up" data-testid="start-ways" style={{ ['--ways' as string]: ways.length }}>
      {ways.map((w) => {
        const c = card[w];
        return (
          <button key={w} type="button" className="hx-start-way" aria-pressed={way === w} onClick={() => onPick(w)} data-testid={`start-way-${w}`} data-recommended={rec === w}>
            <span className="hx-start-way-head">
              {c.icon}
              <span className="hx-start-way-dot" aria-hidden="true" />
            </span>
            <span className="hx-start-text">
              <b>
                {c.title}
                {c.badge && <span className={`hx-start-way-badge ${rec === w ? 'is-recommended' : ''}`}>{c.badge}</span>}
              </b>
              <span>{c.detail}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

type TodoStep = { title: ReactNode; body?: ReactNode };

/** The picked way's steps: the first is current until a sign-in is approved, then the last. */
function WayPanel({ way, device, appIn, email }: { way: Way; device: Device; appIn: boolean; email: string }) {
  const install = installCommand();
  const steps: TodoStep[] =
    way !== 'cli'
      ? [
          {
            title: 'Download and install it',
            body:
              device === 'mobile' ? (
                <span className="hx-faint">{way === 'mac' ? 'On your Mac' : 'On your PC'}: sign in at cmx.sh and download it there.</span>
              ) : way === 'mac' ? (
                <AppDownload platform="mac" className="hx-btn hx-btn-primary hx-btn-sm">
                  {Icon.apple} Download for Mac
                </AppDownload>
              ) : (
                <AppDownload platform="windows" className="hx-btn hx-btn-primary hx-btn-sm">
                  <IconBrandWindows size={16} stroke={1.5} aria-hidden /> Download for Windows
                </AppDownload>
              ),
          },
          { title: `Sign in as ${email}`, body: <span className="hx-faint">It opens this browser once to approve it.</span> },
          { title: 'Use Magic Setup', body: <span className="hx-faint">Or Not now, and set things up yourself.</span> },
        ]
      : [
          ...(device === 'windows'
            ? [
                {
                  title: 'Turn on WSL',
                  body: (
                    <>
                      <span className="hx-faint">In PowerShell, then open Ubuntu from the Start menu and carry on there.</span>
                      <Command text="wsl --install" testid="start-wsl" />
                    </>
                  ),
                },
              ]
            : []),
          { title: 'Install cmx', body: <Command text={install} testid="start-install" /> },
          { title: 'Run cmx', body: <span className="hx-faint">It opens this browser once to approve it.</span> },
          { title: 'Use Magic Setup', body: <span className="hx-faint">Then a coding agent can finish anything left.</span> },
        ];
  const current = appIn ? steps.length - 1 : 0;
  const waiting = appIn ? 'Signed in. Waiting for setup to finish…' : PLACE[way].waiting;
  return (
    <section className="hx-start-app" data-testid={`start-${way}`} data-signed-in={appIn}>
      <ol className="hx-start-todo">
        {steps.map((s, i) => (
          <li key={i} data-state={i < current ? 'done' : i === current ? 'current' : 'next'}>
            <span className="hx-step-chip">{i + 1}</span>
            <span>
              <b>{s.title}</b>
              {s.body}
            </span>
          </li>
        ))}
      </ol>
      <div className="hx-start-wait" role="status" data-testid="start-wait">
        <span className={`hx-pulse ${appIn ? 'is-green' : ''}`} aria-hidden="true" />
        {waiting}
      </div>
    </section>
  );
}

function Command({ text, testid }: { text: string; testid: string }) {
  return <CodeRow text={text} className="hx-start-cmd" codeTestid={`${testid}-cmd`} copyTestid={`${testid}-copy`} />;
}
